import { Input, type InputProps } from 'antd';
import type * as React from 'react';
import { useCallback } from 'react';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

export interface ITextInputProps
  extends Omit<InputProps, 'name' | 'onChange' | 'onBlur'> {
  name: string;
  onChange?: (value: string | null | undefined) => void;
  onBlur?: (value: string | null | undefined) => void;
}

export function TextInput(props: ITextInputProps) {
  const {
    name,
    onChange: onAfterChange,
    onBlur: onAfterBlur,
    ...inputProps
  } = props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e);
      onAfterChange?.(e.target.value);
    },
    [onChange, onAfterChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur();
      onAfterBlur?.(e.target.value);
    },
    [onBlur, onAfterBlur],
  );

  const accessibility = useFormItemAccessibility();

  return (
    <Input
      suffix={<span aria-hidden="true" />} // See https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
      size="large"
      disabled={visuallyDisabled}
      {...accessibility.attributes}
      {...inputProps}
      value={inputProps.value ?? value}
      ref={ref}
      onChange={handleChange}
      onBlur={handleBlur}
      name={name}
    />
  );
}
